import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Row,
  Col,
  Tooltip,
  Empty,
  Modal,
  Radio,
  Alert,
  Divider,
} from "antd";
import {
  PlusCircleOutlined,
  ArrowRightOutlined,
  InfoCircleOutlined,
  SettingFilled,
} from "@ant-design/icons";
import HistoryCampaignCardContent from "../components/common/HistoryCampaignCardContent";
import Title from "antd/es/typography/Title";
import { ChannelEnum } from "../constants/enums";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RoutePaths } from "../routes/route";
import {
  CampaignRegisteredChannel,
  CompletedCampaignViewModel,
  createCampaignRegisteredChannel,
} from "../models/campaign";
import Cookies from "js-cookie";
import WhatsAppConfigModal from "../components/common/WhatsappConfigModal";
import CustomModal from "../components/common/Modal";
import { useBrandStore } from "../store/brandStore";
import { campaignService } from "../services/campaignService";
import ManageConfigurationModal from "../components/common/ManageConfigurationModal";
import Scan from "../components/common/Scan";
import { CardData } from "../models/campaignPerformances";
import moment from "moment";
import { useQueryNavigator } from "../utils/navigate";
import NotAllowed from "./NotAllowed";

const initialCardData: CardData[] = [
  {
    key: "card-data-1",
    title: "Revenue via Rewards",
    value: "$0",
    icon: "/icon/icon-sack-dollar.svg",
    order: 0,
    tooltip: (
      <>
        Total revenue generated from customers who received and
        <br />
        responded to this campaign (generated with with voucher or point usage)
      </>
    ),
  },
  {
    key: "card-data-2",
    title: "Revenue from Campaign",
    value: "$0",
    order: 0,

    icon: "/icon/icon-revenue-campaign.svg",
    tooltip: (
      <>
        Total revenue generated from campaign activities based on targeted
        members spending
      </>
    ),
  },
  {
    key: "card-data-3",
    title: "Return of Investment (ROI)",
    value: "0%",
    icon: "/icon/icon-roi.svg",
    tooltip: (
      <>
        Return on Investment = (Revenue – Cost) / Cost.
        <br />
        Based on the total campaign cost and revenue.
      </>
    ),
  },
  {
    key: "card-data-4",
    title: "Click Through Rate (CTR)",
    value: "0%",
    icon: "/icon/icon-ctr.svg",
    tooltip: (
      <>
        Click-through rate = (Number of clicks / Messages delivered)
        <br />× 100%
      </>
    ),
  },
];

const TargetMarketingHome: React.FC = () => {
  // =====================
  // State Declarations
  // =====================

  // UI & State Management
  const [loading, setLoading] = useState(true);
  const [loadingTopCampaign, setLoadingTopCampaign] = useState(false);
  const [noData, setNoData] = useState(false);
  const isMobile = window.innerWidth < 768;

  const [selectedChannel, setSelectedChannel] = useState<ChannelEnum | null>(
    null,
  );

  const navigate = useNavigate();
  const location = useLocation();
  const [campaigns, setCampaigns] = useState<
    CompletedCampaignViewModel[] | null
  >(null);
  const [cardData, setCardData] = useState<CardData[]>(initialCardData);
  const [channelConfigurationWhatsapp, setChannelConfigurationWhatsapp] =
    useState(false);
  const [isModalSuccessVisible, setIsModalSuccessVisible] = useState(false);

  const [selectedCampaign, setSelectedCampaign] =
    useState<CompletedCampaignViewModel | null>(null);
  const [registeredChannel, setRegisteredChannel] = useState<
    CampaignRegisteredChannel[] | null
  >(null);
  const [selectedRegisteredChannel, setSelectedRegisteredChannel] =
    useState<CampaignRegisteredChannel | null>(null);
  const [isModalCreateCampaignVisible, setIsModalCreateCampaignVisible] =
    useState(false);
  const isMobileOrTablet = window.innerWidth <= 1024;
  const [isWhatsappConfigModalVisible, setIsWhatsappConfigModalVisible] =
    useState(false);
  const [configurationModalVisible, setConfigurationModalVisible] =
    useState(false);
  const { brandId, setCampaignInfo } = useBrandStore();
  const [scanModalVisible, setScanModalVisible] = useState(false);
  const [actionConfiguration, setActionConfiguration] = useState("");
  const [notAllowed, setNotAllowed] = useState(false);
  const navigateWithQuery = useQueryNavigator();

  const channels: Array<{
    key: ChannelEnum;
    title: string;
    subtitle: string;
    disabled: boolean;
    features: string;
    price: number;
    icon: React.ReactNode;
  }> = [
    {
      key: ChannelEnum.Sms,
      title: "SMS Campaign",
      subtitle: "Send short, direct messages instantly for quick communication",
      disabled: false,
      features: "Twilio",
      price: Number(process.env.REACT_APP_SMS_COST),
      icon: (
        <i
          className="icon-sms"
          style={{
            fontSize: isMobile ? 28 : 36,
            width: isMobile ? 40 : 100,
            height: isMobile ? 40 : 100,
            display: isMobile ? "flex" : "unset",
            alignItems: isMobile ? "center" : "unset",
            justifyContent: isMobile ? "center" : "unset",
          }}
        />
      ),
    },
    {
      key: ChannelEnum.WhatsAppStandard,
      title: "WhatsApp Standard",
      subtitle: "Personalized chats with flexible setup",
      disabled: false,
      features: "",
      price: Number(process.env.REACT_APP_WHATSAPP_COST),
      icon: (
        <i
          className="icon-whatsapp"
          style={{
            fontSize: isMobile ? 28 : 36,
            width: isMobile ? 40 : 100,
            height: isMobile ? 40 : 100,
            display: isMobile ? "flex" : "unset",
            alignItems: isMobile ? "center" : "unset",
            justifyContent: isMobile ? "center" : "unset",
          }}
        />
      ),
    },
    {
      key: ChannelEnum.WhatsAppPremium,
      title: "WhatsApp Premium",
      subtitle: "Reliable enterprise messaging with advanced API features",
      disabled: true,
      features: "Twilio",
      price: Number(process.env.REACT_APP_WHATSAPP_PREMIUM_COST),
      icon: (
        <i
          className="icon-whatsapp"
          style={{
            fontSize: isMobile ? 28 : 36,
            width: isMobile ? 40 : 100,
            height: isMobile ? 40 : 100,
            display: isMobile ? "flex" : "unset",
            alignItems: isMobile ? "center" : "unset",
            justifyContent: isMobile ? "center" : "unset",
          }}
        />
      ),
    },
    {
      key: ChannelEnum.Email,
      title: "Email Campaign",
      subtitle: "Send detailed messages with rich formatting and visuals",
      disabled: true,
      features: "",
      price: 0.03,
      icon: (
        <i
          className="icon-mail"
          style={{
            fontSize: isMobile ? 28 : 36,
            width: isMobile ? 40 : 100,
            height: isMobile ? 40 : 100,
            display: isMobile ? "flex" : "unset",
            alignItems: isMobile ? "center" : "unset",
            justifyContent: isMobile ? "center" : "unset",
          }}
        />
      ),
    },
  ];

  const fetchCampaignData = async (): Promise<
    CompletedCampaignViewModel[] | null
  > => {
    try {
      setLoadingTopCampaign(true);
      if (!brandId) return null;

      const response = await campaignService.getTopPerformingCampaigns(
        brandId,
        3,
      );

      return response as CompletedCampaignViewModel[];
    } catch (error) {
      console.error("Error fetching history campaign:", error);
      return null;
    } finally {
      setLoadingTopCampaign(false);
    }
  };

  const fetchCampaignRegisteredChannel = async () => {
    try {
      if (!brandId) return;

      const response = await campaignService.getRegisteredChannel(brandId);
      setRegisteredChannel(response);
      if (
        response &&
        response?.length !== 0
        // && response.some((item) => item.Status === true)
      ) {
        setChannelConfigurationWhatsapp(true);
      }
    } catch (error) {
      console.error("Error fetching history campaign:", error);
    }
  };

  const handleSubmitCreate = () => {
    if (
      !channelConfigurationWhatsapp &&
      selectedChannel === ChannelEnum.WhatsAppStandard
    ) {
      setIsWhatsappConfigModalVisible(true);
    } else if (
      !registeredChannel?.some((item) => item.Status === true) &&
      selectedChannel === ChannelEnum.WhatsAppStandard
    ) {
      setConfigurationModalVisible(true);
      setIsModalCreateCampaignVisible(false);
    } else {
      navigateWithQuery(
        RoutePaths.IFRAME_CREATE_CAMPAIGN,
        "campaign_channel",
        String(selectedChannel),
      );
    }
  };

  const handleActivateWhatsApp = async (
    values: any,
    reactive: boolean = false,
  ) => {
    if (reactive) {
      if (selectedRegisteredChannel?.Id) {
        const reactive =
          await campaignService.reactiveCampaignRegisteredChannelStatus(
            selectedRegisteredChannel?.Id,
          );
      }
    } else {
      const model = createCampaignRegisteredChannel({
        email: values.email,
        contactIdd: values.contactIdd,
        contactNumber: values.contactNumber,
        clientId: values.clientId,
        brandId: brandId ?? "",
        channel: ChannelEnum.WhatsAppStandard,
      });

      await campaignService.createCampaignRegisteredChannel(model);
    }

    setIsWhatsappConfigModalVisible(false);
    setIsModalSuccessVisible(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchCampaignData().then((data: any) => {
      if (data != null) {
        setCampaigns(data);
        setSelectedCampaign(data[0]);
      }
    });
    fetchCampaignRegisteredChannel();
    if (brandId) {
      campaignService.getCampaignInfo(brandId).then((info) => {
        if (info) {
          setCampaignInfo(info);
        }
      });
    }
  }, [brandId]);

  useEffect(() => {
    if (selectedCampaign) {
      setCardData((prev) =>
        prev.map((c) => {
          if (c.key === "card-data-1")
            return {
              ...c,
              value:
                "$" + selectedCampaign?.RevenueRewards.toLocaleString("en-US"),
              order: selectedCampaign?.TotalOrderRewards,
            };
          if (c.key === "card-data-2")
            return {
              ...c,
              value:
                "$" + selectedCampaign?.RevenueCampaign.toLocaleString("en-US"),
              order: selectedCampaign?.TotalOrderCampaign,
            };
          if (c.key === "card-data-3")
            return {
              ...c,
              value: selectedCampaign.ROI.toLocaleString("en-US") + "%",
            };
          if (c.key === "card-data-4")
            return {
              ...c,
              value: selectedCampaign.CTR.toLocaleString("en-US") + "%",
            };
          return c;
        }),
      );
    }
  }, [selectedCampaign]);

  useEffect(() => {
    const API_BASE_URL = process.env.REACT_APP_API_URL || "";

    const isDevelopment =
      API_BASE_URL === "" ||
      API_BASE_URL === "https://sit-api-crm.getzpay.com" ||
      API_BASE_URL.includes("local");

    const allowedBrandIds = new Set([
      "019086f7-e9f8-41fd-ae93-90a189499a5a",
      "ae917874-9936-4916-bcf2-a4953b667130", // Crab
      "aeba3bed-2ba8-4046-ac51-0674ae9662ba", // San re
      "b11b90ed-f420-48d7-a4c2-75ed1e824f99", // Opah satay
      "630957c2-f71b-4424-9a03-cae75c1dbc9d", // Carl's Jr
      "fa082296-c4f3-4b0a-ac1c-8c8902e58dcc", // City Peramakan
      "cf59d08f-83d6-4947-ac98-601f15540c19", // Eminami
      "f66941a1-fefb-40e7-b1fc-4bb2676e1bbe", // sample 1
      "ee9cf44e-f21c-4c31-b3cc-32a25339b53d", // T Restaurant
      "9db7ba8a-1e81-4938-93d0-5af5281e63c4", // Typhoon
    ]);
    if (!isDevelopment && brandId && !allowedBrandIds.has(brandId)) {
      setNotAllowed(true);
    }
  }, [brandId]);

  if (notAllowed) {
    return <NotAllowed />;
  }

  return (
    <>
      <div>
        <Row
          align={"middle"}
          style={{ padding: isMobileOrTablet ? "0px 24px" : "unset" }}
        >
          <Col
            xs={24}
            md={12}
            xl={12}
            lg={12}
            style={{
              fontSize: 20,
              textAlign: isMobileOrTablet ? "center" : undefined,
            }}
          >
            Targeted Marketing Campaigns
          </Col>
          <Col
            xs={24}
            md={12}
            xl={12}
            lg={12}
            style={{
              marginTop: 10,
            }}
          >
            <Row justify={"end"} style={{ gap: 8 }}>
              <Col
                span={24}
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                <Button
                  type="primary"
                  block
                  onClick={() => setIsModalCreateCampaignVisible(true)}
                  style={{
                    width: isMobileOrTablet ? "100%" : 210,
                  }}
                >
                  <PlusCircleOutlined />
                  <span> Create Campaigns </span>
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <Card
          loading={loading}
          title={
            <Row justify="space-between" align="middle">
              <Col xs={24} md={16} xl={16}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <span style={{ fontSize: 20 }}>Top Campaign Performers</span>
                  <Tooltip
                    placement={isMobileOrTablet ? "bottom" : "rightBottom"}
                    title={
                      <>
                        Displaying the top-performing campaigns{" "}
                        {isMobileOrTablet && <br />}based on metrics like
                        revenue,
                        <br />
                        ROI, and click-through rate.{" "}
                        {isMobileOrTablet && <br />}This section updates
                        automatically
                        <br />
                        after campaigns are sent. Maximum 3 will be shown.
                      </>
                    }
                  >
                    <InfoCircleOutlined
                      style={{
                        fontSize: 16,
                        marginLeft: 6,
                        cursor: "pointer",
                        color: "#888",
                      }}
                    />
                  </Tooltip>
                </span>
                {!isMobile && (
                  <Button
                    type="primary"
                    style={{
                      backgroundColor: "#E9F4FB",
                      color: "#1565C0",
                      fontSize: isMobile ? "10px" : "unset",
                      marginLeft: 10,
                    }}
                  >
                    Last Update:{" "}
                    <strong>
                      {moment().subtract(1, "days").format("YYYY-MM-DD")}{" "}
                      23:59:59
                    </strong>
                  </Button>
                )}
              </Col>
              {isMobile && (
                <Col
                  xs={24}
                  md={16}
                  xl={16}
                  style={{ marginTop: 5, marginBottom: 5 }}
                >
                  <Button
                    type="primary"
                    style={{
                      backgroundColor: "#E9F4FB",
                      color: "#1565C0",
                      width: "100%",
                    }}
                  >
                    Last Update:{" "}
                    <strong>
                      {moment().subtract(1, "days").format("YYYY-MM-DD")}{" "}
                      23:59:59
                    </strong>
                  </Button>
                </Col>
              )}
              {/* update to count campaign is more than 3 */}
              {!noData && campaigns && campaigns?.length >= 3 && (
                <Col xs={24} md={6} xl={8}>
                  <Button
                    color="primary"
                    variant="outlined"
                    style={{
                      width: isMobileOrTablet ? "100%" : 210,
                      float: isMobileOrTablet ? undefined : "inline-end",
                      marginTop: isMobileOrTablet ? 5 : "unset",
                    }}
                    onClick={() =>
                      navigate(
                        `${RoutePaths.IFRAME_TARGET_MARKETING_ALL}${location.search}`,
                      )
                    }
                  >
                    <b>
                      Show All Campaigns <ArrowRightOutlined />
                    </b>
                  </Button>
                </Col>
              )}
            </Row>
          }
          style={{ marginTop: 10 }}
        >
          {!noData && (
            // <Row gutter={[12, 12]}>
            //   {campaigns?.map((c: any) => (
            //     <Col key={c.CampaignId} xl={4} md={4} xs={24}>
            //       <Button
            //         type={
            //           selectedCampaign?.CampaignId === c.CampaignId
            //             ? "primary"
            //             : "default"
            //         }
            //         size="middle"
            //         block
            //         style={{ width: "100%" }}
            //         onClick={() => setSelectedCampaign(c)}
            //       >
            //         <span
            //           style={{
            //             display: "inline-block",
            //             maxWidth: "100%",
            //             overflow: "hidden",
            //             textOverflow: "ellipsis",
            //             whiteSpace: "nowrap",
            //           }}
            //         >
            //           {c.CampaignName}
            //         </span>
            //       </Button>
            //     </Col>
            //   ))}
            // </Row>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
              }}
            >
              {campaigns?.map((c: any) => (
                <Button
                  key={c.CampaignId}
                  type={
                    selectedCampaign?.CampaignId === c.CampaignId
                      ? "primary"
                      : "default"
                  }
                  size="middle"
                  onClick={() => setSelectedCampaign(c)}
                  style={{
                    width: isMobile ? "100%" : "auto",
                    padding: "8px 18px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    borderRadius: 8,
                  }}
                >
                  {c.CampaignName}
                </Button>
              ))}
            </div>
          )}

          {!noData && (
            <Row gutter={[12, 12]} style={{ marginTop: 20 }}>
              {cardData?.map((item, index) => {
                return (
                  <Col
                    md={12}
                    xl={6}
                    key={index}
                    style={{ width: isMobile ? "100%" : "unset" }}
                  >
                    <Card className="card-member" loading={loadingTopCampaign}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          height: 70,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 14,
                              lineHeight: "1.2",
                            }}
                          >
                            {item.title}
                            {item.tooltip && (
                              <Tooltip title={item.tooltip}>
                                <InfoCircleOutlined
                                  style={{
                                    fontSize: 14,
                                    marginLeft: 6,
                                    color: "#888",
                                  }}
                                />
                              </Tooltip>
                            )}
                          </div>
                          <div style={{ fontSize: 24, fontWeight: 700 }}>
                            {item.value}
                          </div>
                          {item.key === "card-data-1" ||
                          item.key === "card-data-2" ? (
                            <Alert
                              className="alert-show-count"
                              description={
                                <div>
                                  <span style={{ color: "#1565C0" }}>
                                    {item.order} Order
                                  </span>
                                </div>
                              }
                              style={{ width: "fit-content" }}
                              type="info"
                            />
                          ) : (
                            <div style={{ height: 25 }}></div>
                          )}
                        </div>
                        <img
                          src={item.icon}
                          alt="icon"
                          width="50"
                          height="50"
                        />
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
          {noData && (
            <Empty
              image={<i className="icon-targetedmarketing" aria-hidden />}
              description={
                <span>
                  No campaign performance data yet. Once your campaigns are
                  sent, you’ll see the top performers here.
                </span>
              }
              className="no-data"
            />
          )}
        </Card>
        <Card
          loading={loading}
          title={
            <Row justify="space-between" align="middle">
              <Col>
                <span style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: 20 }}>History Campaign</span>
                </span>
              </Col>
            </Row>
          }
          className="history-campaign-card"
          style={{ marginTop: 10 }}
        >
          {" "}
          {!noData && <HistoryCampaignCardContent />}
          {noData && (
            <Empty
              image={<i className="icon-history" aria-hidden />}
              description={
                <span>
                  There are no campaign yet. Create a new campaign to get
                  started!
                </span>
              }
              className="no-data"
            />
          )}
        </Card>
      </div>
      <Modal
        open={isModalCreateCampaignVisible}
        onCancel={() => setIsModalCreateCampaignVisible(false)}
        title={
          <div className="flex flex-col" style={{ textAlign: "center" }}>
            <Title level={4} style={{ margin: 0, flex: "center" }}>
              Select Your Campaign Channel
            </Title>
            {/* <Radio.Group
              value={channelConfigurationWhatsapp}
              onChange={onChange}
            >
              <Radio value={true}>Enabled</Radio>
              <Radio value={false}>Disabled</Radio>
            </Radio.Group> */}
            <div
              style={{
                fontSize: 11,
                height: 32,
                overflow: "hidden",
                color: "grey",
              }}
            >
              Select the communication channel to use for this campaign. Only
              one can be used per send.
            </div>
          </div>
        }
        footer={null}
        width={isMobile ? "100%" : 1200}
        height={isMobile ? 650 : 800}
        centered
        closable={false}
        maskClosable={false}
      >
        {!isMobile && (
          <Row gutter={[16, 16]} justify="center">
            <Col xs={24} sm={24} md={24}>
              <Radio.Group
                onChange={(e) => setSelectedChannel(e.target.value)}
                value={selectedChannel}
                style={{ width: "100%" }}
              >
                <Row gutter={[16, 16]} justify="center">
                  {channels.map((c) => (
                    <Col xs={24} sm={24} md={12} xl={6} key={c.key}>
                      <Radio.Button
                        value={c.key}
                        style={{ border: "none", padding: 0 }}
                        disabled={c.disabled}
                      >
                        <Card
                          hoverable={!c.disabled}
                          style={{
                            height: isMobileOrTablet ? 320 : 280,
                            width: "100%",
                            textAlign: "center",
                            backgroundColor: c.disabled
                              ? "#f5f5f5"
                              : selectedChannel === c.key
                                ? "#E7EFF9"
                                : "#FFFFFF",
                            border: c.disabled
                              ? "1px dashed #d9d9d9"
                              : selectedChannel === c.key
                                ? "4px solid #1565C0"
                                : "1px solid #f0f0f0",
                            cursor: c.disabled ? "not-allowed" : "pointer",
                            opacity: c.disabled ? 0.6 : 1,
                          }}
                        >
                          {c.disabled && (
                            <div className="ribbon-style">
                              <span className="ribbon-style-text">
                                COMING SOON
                              </span>
                            </div>
                          )}
                          <div
                            style={{
                              color: c.disabled
                                ? "#bfbfbf"
                                : selectedChannel === c.key
                                  ? "#1565C0"
                                  : "#8c8c8c",
                              marginBottom: 8,
                            }}
                          >
                            {c.icon}
                          </div>
                          <div
                            style={{
                              fontWeight: 600,
                              color: c.disabled ? "#bfbfbf" : "#000",
                            }}
                          >
                            {c.title}
                          </div>
                          <div
                            style={{
                              fontSize: 11,
                              overflow: "hidden",
                              color: c.disabled ? "#bfbfbf" : "grey",
                              fontWeight: "normal",
                            }}
                          >
                            {c.subtitle}
                            {!c.disabled && (
                              <Row
                                gutter={[12, 12]}
                                style={{ marginBottom: 5 }}
                              >
                                {c.features != "" && (
                                  <Col md={24} xl={11}>
                                    <Button className="w-100" size="small">
                                      {c.features}
                                    </Button>
                                  </Col>
                                )}

                                <Col md={24} xl={c.features === "" ? 24 : 13}>
                                  <Button className="w-100" size="small">
                                    {c.price} / message
                                  </Button>
                                </Col>
                              </Row>
                            )}

                            {c.key === ChannelEnum.WhatsAppStandard &&
                              !channelConfigurationWhatsapp && (
                                <Alert
                                  className="alert-required-configuration"
                                  description={
                                    <>Configuration required before use.</>
                                  }
                                  type="error"
                                />
                              )}
                            {c.key === ChannelEnum.WhatsAppStandard &&
                              channelConfigurationWhatsapp && (
                                <Button
                                  size="middle"
                                  type="primary"
                                  disabled={false}
                                  style={{
                                    fontSize: 12,
                                  }}
                                  onClick={() => {
                                    setConfigurationModalVisible(true);
                                    setIsModalCreateCampaignVisible(false);
                                  }}
                                >
                                  <SettingFilled
                                    style={{
                                      color: "white",
                                      marginRight: 5,
                                    }}
                                  />
                                  Manage Configuration{" "}
                                </Button>
                              )}
                          </div>
                        </Card>
                      </Radio.Button>
                    </Col>
                  ))}
                </Row>
              </Radio.Group>
            </Col>
          </Row>
        )}
        {isMobile && (
          <Row gutter={[16, 16]} justify="center">
            <Col xs={24} sm={24} md={24}>
              <Radio.Group
                onChange={(e) => setSelectedChannel(e.target.value)}
                value={selectedChannel}
                style={{ width: "100%" }}
              >
                <Row gutter={[10, 10]} justify="center">
                  {channels.map((c) => (
                    <Col xs={24} sm={24} md={8} lg={6} key={c.key}>
                      <Radio.Button
                        value={c.key}
                        style={{ border: "none", padding: 0 }}
                        disabled={c.disabled}
                      >
                        <Card
                          hoverable={!c.disabled}
                          style={{
                            minHeight: isMobile ? 80 : 160,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            padding: "5px",
                            textAlign: "left",
                            width: "100%",
                            backgroundColor: c.disabled
                              ? "#f5f5f5"
                              : selectedChannel === c.key
                                ? "#E7EFF9"
                                : "#FFFFFF",
                            border: c.disabled
                              ? "1px dashed #d9d9d9"
                              : selectedChannel === c.key
                                ? "4px solid #1565C0"
                                : "1px solid #f0f0f0",
                            cursor: c.disabled ? "not-allowed" : "pointer",
                            opacity: c.disabled ? 0.6 : 1,
                          }}
                          className="card-select-channel-mobile"
                        >
                          {c.disabled && (
                            <div className="ribbon-style">
                              {" "}
                              <span className="ribbon-style-text">
                                COMING SOON
                              </span>{" "}
                            </div>
                          )}{" "}
                          <Row align="middle">
                            <Col
                              span={6}
                              style={{
                                display: "flex",
                                justifyContent: "center",
                              }}
                            >
                              <div
                                style={{
                                  color: c.disabled
                                    ? "#bfbfbf"
                                    : selectedChannel === c.key
                                      ? "#1565C0"
                                      : "#8c8c8c",
                                  marginBottom: 8,
                                }}
                              >
                                {c.icon}
                              </div>
                            </Col>
                            <Col span={18}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: c.disabled ? "#bfbfbf" : "#000",
                                }}
                              >
                                {c.title}
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  overflow: "hidden",
                                  color: c.disabled ? "#bfbfbf" : "grey",
                                  fontWeight: "normal",
                                }}
                              >
                                {c.subtitle}
                                {!c.disabled && (
                                  <Row
                                    gutter={[12, 12]}
                                    style={{ marginBottom: 5 }}
                                  >
                                    <Col span={11}>
                                      <Button
                                        className="w-100"
                                        size="small"
                                        style={{ fontSize: 12 }}
                                      >
                                        {c.features}
                                      </Button>
                                    </Col>
                                    <Col span={13}>
                                      <Button
                                        className="w-100"
                                        size="small"
                                        style={{ fontSize: 12 }}
                                      >
                                        {c.price} / message
                                      </Button>
                                    </Col>
                                  </Row>
                                )}

                                {c.key === ChannelEnum.WhatsAppStandard &&
                                  !channelConfigurationWhatsapp && (
                                    <Alert
                                      className="alert-required-configuration"
                                      description={
                                        <>Configuration required before use.</>
                                      }
                                      type="error"
                                    />
                                  )}
                                {c.key === ChannelEnum.WhatsAppStandard &&
                                  channelConfigurationWhatsapp && (
                                    <Button
                                      size="small"
                                      type="primary"
                                      style={{
                                        fontSize: 12,
                                      }}
                                      onClick={() => {
                                        setConfigurationModalVisible(true);
                                        setIsModalCreateCampaignVisible(false);
                                      }}
                                    >
                                      <SettingFilled
                                        style={{
                                          color: "white",
                                          marginRight: 5,
                                        }}
                                      />
                                      Manage Configuration{" "}
                                    </Button>
                                  )}
                              </div>
                            </Col>
                          </Row>
                        </Card>
                      </Radio.Button>
                    </Col>
                  ))}
                </Row>
              </Radio.Group>
            </Col>
          </Row>
        )}
        {isMobile && <Divider style={{ margin: "12px 0" }} />}
        <Row
          gutter={[16, 16]}
          style={{ marginTop: isMobile ? "unset" : 50 }}
          justify="center"
        >
          <Col span={12} style={{ textAlign: "right" }}>
            <Button
              key="cancel"
              size="large"
              style={{ width: isMobileOrTablet ? 150 : 210 }}
              onClick={() => {
                setIsModalCreateCampaignVisible(false);
                setSelectedChannel(null);
              }}
            >
              Cancel
            </Button>
          </Col>
          <Col span={12}>
            <Button
              size="large"
              key="submit"
              style={{ width: isMobileOrTablet ? 150 : 210 }}
              type="primary"
              disabled={!selectedChannel}
              onClick={handleSubmitCreate}
            >
              {selectedChannel === ChannelEnum.WhatsAppStandard &&
              !channelConfigurationWhatsapp
                ? "Activate WhatsApp"
                : "Submit"}
            </Button>
          </Col>
        </Row>
      </Modal>
      <Modal></Modal>
      <WhatsAppConfigModal
        open={isWhatsappConfigModalVisible}
        onCancel={() => setIsWhatsappConfigModalVisible(false)}
        onContinue={(values) => {
          handleActivateWhatsApp(values);
        }}
        defaultEmail={registeredChannel?.[0]?.Email ?? ""}
      />
      <CustomModal
        type="success"
        title={"WhatsApp account successfully linked"}
        message={"Your WhatsApp account is now activated and ready to use."}
        isVisible={isModalSuccessVisible}
        onClose={() => setIsModalSuccessVisible(false)}
        successButtonText="Start Creating Campaign"
        onSuccessClick={() => {
          navigateWithQuery(
            RoutePaths.IFRAME_CREATE_CAMPAIGN,
            "campaign_channel",
            ChannelEnum.WhatsAppStandard,
          );
        }}
        cancelButtonText="Back to Homepage"
        onCancelClick={() => {
          window.location.reload();
        }}
      />
      <ManageConfigurationModal
        open={configurationModalVisible}
        onClose={() => setConfigurationModalVisible(false)}
        accountEmail={registeredChannel?.[0]?.Email || ""}
        phones={registeredChannel ?? []}
        onAddNew={() => {
          setActionConfiguration("add");
          setScanModalVisible(true);
        }}
        onReactivate={(id: number | undefined) => {
          const foundItem = (registeredChannel ?? []).find(
            (item) => item.Id === id,
          );

          if (foundItem) {
            setSelectedRegisteredChannel(foundItem);
          }
          setActionConfiguration("reactive");
          setScanModalVisible(true);
        }}
      />
      <Scan
        title={actionConfiguration}
        open={scanModalVisible}
        onCancel={() => setScanModalVisible(false)}
        onContinue={(values) => {
          handleActivateWhatsApp(
            values,
            actionConfiguration === "reactive" ? true : false,
          );
        }}
        defaultEmail={registeredChannel?.[0]?.Email ?? ""}
        defaultContactIdd={selectedRegisteredChannel?.ContactIdd}
        defaultContactNumber={selectedRegisteredChannel?.ContactNumber}
        registeredChannel={registeredChannel}
      />
    </>
  );
};

export default TargetMarketingHome;
